/**
 * A sheet that comes up from the bottom of a phone.
 *
 * It is the phone's answer to a menu and to a dialog: a panel anchored to the
 * bottom edge, where a thumb is, with its title in view and a close control
 * of its own. The page behind it does not scroll, the sheet itself does, and
 * it never grows past the height of the screen.
 *
 * It closes on Escape, on a tap outside it, and on its own close control.
 * None of that touches browser history: a sheet is not a page, so Back still
 * means the previous page and never means "close this".
 *
 * The bottom of the sheet keeps clear of the home indicator on a phone that
 * has one; see the safe-area rules in `styles.css`.
 */

import { useEffect, useRef, type ReactNode } from "react";

import { IconButton, classes } from "../primitives.tsx";

export function Sheet({
  title,
  open,
  onClose,
  children,
  testId,
  footer,
}: {
  readonly title: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly children: ReactNode;
  readonly testId?: string;
  readonly footer?: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);

  // Escape closes it, and the page behind it holds still while it is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const held = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = held;
    };
  }, [open, onClose]);

  // The first thing inside it takes focus, so a keyboard and a screen reader
  // both arrive where the sheet is rather than where the page was — and the
  // control that opened it takes the focus back when it closes, so a keyboard
  // is not returned to the top of the page.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const first = panel.current?.querySelector<HTMLElement>(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])",
    );
    first?.focus();
    return () => {
      if (opener !== null && document.contains(opener)) opener.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      {/* The page behind it, dimmed. A tap here closes the sheet. */}
      <button
        type="button"
        aria-label="Close"
        tabIndex={-1}
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
      />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={testId}
        className={classes(
          "arc-sheet relative max-h-[85vh] w-full overflow-y-auto rounded-t-arc",
          "border-t border-divider bg-paper shadow-xl",
        )}
      >
        <header className="sticky top-0 flex items-center justify-between gap-3 border-b border-divider bg-paper px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <IconButton icon="close" label={`Close ${title}`} onClick={onClose} />
        </header>
        <div className="px-4 py-3">{children}</div>
        {footer !== undefined && (
          <div className="border-t border-divider px-4 py-3">{footer}</div>
        )}
      </div>
    </div>
  );
}

/** One line in a sheet: a destination, a choice, or an action. */
export function SheetRow({
  children,
  onClick,
  selected = false,
  icon,
  testId,
}: {
  readonly children: ReactNode;
  readonly onClick: () => void;
  readonly selected?: boolean;
  readonly icon?: ReactNode;
  readonly testId?: string;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      aria-current={selected ? "true" : undefined}
      className={classes(
        "flex min-h-11 w-full items-center gap-3 rounded-arc px-2 py-2 text-left",
        selected ? "font-semibold text-accent" : "hover:bg-selected",
      )}
      onClick={onClick}
    >
      {icon !== undefined && (
        <span aria-hidden="true" className="w-5 shrink-0 text-muted">
          {icon}
        </span>
      )}
      <span className="min-w-0 flex-1">{children}</span>
      {selected && (
        <span aria-hidden="true" className="text-accent">
          ✓
        </span>
      )}
    </button>
  );
}

/** A heading inside a sheet, above a group of rows. */
export function SheetGroup({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="mb-3">
      <h3 className="px-2 pb-1 text-sm font-medium text-muted">{label}</h3>
      <div role="menu" aria-label={label} className="flex flex-col">
        {children}
      </div>
    </div>
  );
}
